import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import JSZip from "jszip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import {
  User, Lock, Search, Settings, ArrowLeft, CheckCircle, XCircle,
  Trash2, LogOut, Clock, Download,
} from "lucide-react";
import { format } from "date-fns";

const passwordChecks = (pw: string) => [
  { label: "At least 8 characters", pass: pw.length >= 8 },
  { label: "Contains uppercase letter", pass: /[A-Z]/.test(pw) },
  { label: "Contains lowercase letter", pass: /[a-z]/.test(pw) },
  { label: "Contains a number", pass: /\d/.test(pw) },
  { label: "Contains a special character", pass: /[^A-Za-z0-9]/.test(pw) },
];

interface SavedSearch {
  id: string;
  query: string;
  source: string;
  model: string | null;
  created_at: string;
}

export default function UserProfile() {
  const [userEmail, setUserEmail] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [searchesLoading, setSearchesLoading] = useState(true);
  const [deletingSearch, setDeletingSearch] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/"); return; }
      setUserEmail(user.email || "");
      setCreatedAt(user.created_at || "");

      const { data } = await supabase
        .from("search_results")
        .select("id, query, source, model, created_at")
        .order("created_at", { ascending: false })
        .limit(100);
      setSearches(data || []);
      setSearchesLoading(false);
    };
    load();
  }, [navigate]);

  const checks = passwordChecks(newPassword);
  const allPass = checks.every((c) => c.pass);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allPass) {
      toast({ title: "Weak password", description: "Meet all requirements.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setChangingPw(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPw(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Password updated", description: "Your password has been changed." });
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  const handleDeleteSearch = async (id: string) => {
    setDeletingSearch(id);
    const { error } = await supabase.from("search_results").delete().eq("id", id);
    if (!error) {
      setSearches((prev) => prev.filter((s) => s.id !== id));
      toast({ title: "Deleted", description: "Search record removed." });
    } else {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    setDeletingSearch(null);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const makeCsv = (headers: string[], rows: (string | number | boolean | null | undefined)[][]) => {
    return [headers.join(","), ...rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  };

  const downloadCsv = (filename: string, headers: string[], rows: string[][]) => {
    const blob = new Blob([makeCsv(headers, rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [exporting, setExporting] = useState<string | null>(null);

  const exportPositions = async () => {
    setExporting("positions");
    const { data } = await supabase.from("positions").select("*").order("created_at", { ascending: false });
    if (data && data.length > 0) {
      const headers = ["Ticker", "Stock Name", "ISIN", "Quantity", "Buy Price", "Price/Share", "Total Value", "Gain %", "Currency", "Created"];
      const rows = data.map(p => [p.ticker, p.stock_name, p.isin, p.quantity, p.buy_price, p.price_per_share, p.total_value, p.gain_pct, p.local_currency, p.created_at]);
      downloadCsv("positions.csv", headers, rows as string[][]);
      toast({ title: "Exported", description: `${data.length} positions exported.` });
    } else {
      toast({ title: "No data", description: "No positions to export.", variant: "destructive" });
    }
    setExporting(null);
  };

  const exportOrders = async () => {
    setExporting("orders");
    const { data } = await supabase.from("orders").select("*").order("created_at", { ascending: false });
    if (data && data.length > 0) {
      const headers = ["ISIN", "Company", "Type", "Order Type", "Quantity", "Limit Price", "Exchange", "Portfolio", "Status", "Date"];
      const rows = data.map(o => [o.isin_code, o.company_name, o.transaction_type, o.order_type, o.shares_quantity, o.limit_price, o.stock_exchange_code, o.customer_portfolio, o.status, o.order_date]);
      downloadCsv("orders.csv", headers, rows as string[][]);
      toast({ title: "Exported", description: `${data.length} orders exported.` });
    } else {
      toast({ title: "No data", description: "No orders to export.", variant: "destructive" });
    }
    setExporting(null);
  };

  const exportSearches = async () => {
    setExporting("searches");
    const { data } = await supabase.from("search_results").select("*").order("created_at", { ascending: false });
    if (data && data.length > 0) {
      const headers = ["Query", "Source", "Model", "Response", "Created"];
      const rows = data.map(s => [s.query, s.source, s.model, s.response_content?.substring(0, 500), s.created_at]);
      downloadCsv("searches.csv", headers, rows as string[][]);
      toast({ title: "Exported", description: `${data.length} searches exported.` });
    } else {
      toast({ title: "No data", description: "No searches to export.", variant: "destructive" });
    }
    setExporting(null);
  };

  const exportWatchlist = async () => {
    setExporting("watchlist");
    const { data } = await supabase.from("watchlist_alerts").select("*").order("created_at", { ascending: false });
    if (data && data.length > 0) {
      const headers = ["Ticker", "Alert Type", "Target Price", "% Threshold", "Triggered", "Notes", "Created"];
      const rows = data.map(w => [w.ticker, w.alert_type, w.target_price, w.pct_threshold, w.triggered, w.notes, w.created_at]);
      downloadCsv("watchlist.csv", headers, rows as string[][]);
      toast({ title: "Exported", description: `${data.length} alerts exported.` });
    } else {
      toast({ title: "No data", description: "No watchlist alerts to export.", variant: "destructive" });
    }
    setExporting(null);
  };

  const exportAll = async () => {
    setExporting("all");
    const zip = new JSZip();
    
    const { data: pos } = await supabase.from("positions").select("*").order("created_at", { ascending: false });
    if (pos?.length) zip.file("positions.csv", makeCsv(["Ticker","Stock Name","ISIN","Quantity","Buy Price","Price/Share","Total Value","Gain %","Currency","Created"], pos.map(p => [p.ticker, p.stock_name, p.isin, p.quantity, p.buy_price, p.price_per_share, p.total_value, p.gain_pct, p.local_currency, p.created_at])));

    const { data: ord } = await supabase.from("orders").select("*").order("created_at", { ascending: false });
    if (ord?.length) zip.file("orders.csv", makeCsv(["ISIN","Company","Type","Order Type","Quantity","Limit Price","Exchange","Portfolio","Status","Date"], ord.map(o => [o.isin_code, o.company_name, o.transaction_type, o.order_type, o.shares_quantity, o.limit_price, o.stock_exchange_code, o.customer_portfolio, o.status, o.order_date])));

    const { data: sr } = await supabase.from("search_results").select("*").order("created_at", { ascending: false });
    if (sr?.length) zip.file("searches.csv", makeCsv(["Query","Source","Model","Response","Created"], sr.map(s => [s.query, s.source, s.model, s.response_content?.substring(0, 500), s.created_at])));

    const { data: wa } = await supabase.from("watchlist_alerts").select("*").order("created_at", { ascending: false });
    if (wa?.length) zip.file("watchlist.csv", makeCsv(["Ticker","Alert Type","Target Price","% Threshold","Triggered","Notes","Created"], wa.map(w => [w.ticker, w.alert_type, w.target_price, w.pct_threshold, w.triggered, w.notes, w.created_at])));

    if (Object.keys(zip.files).length === 0) {
      toast({ title: "No data", description: "No data found to export.", variant: "destructive" });
      setExporting(null);
      return;
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `portfolio-export-${format(new Date(), "yyyy-MM-dd")}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${Object.keys(zip.files).length} files exported as ZIP.` });
    setExporting(null);
  };
  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Account Settings</h1>
            <p className="text-sm text-muted-foreground">{userEmail}</p>
          </div>
        </div>

        <Tabs defaultValue="account" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="account" className="gap-1.5">
              <User className="h-4 w-4" /> Account
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-1.5">
              <Lock className="h-4 w-4" /> Security
            </TabsTrigger>
            <TabsTrigger value="searches" className="gap-1.5">
              <Search className="h-4 w-4" /> Searches
            </TabsTrigger>
            <TabsTrigger value="export" className="gap-1.5">
              <Download className="h-4 w-4" /> Export
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-4 w-4" /> Account Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-1">
                  <Label className="text-muted-foreground text-xs">Email</Label>
                  <p className="font-medium">{userEmail}</p>
                </div>
                {createdAt && (
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs">Member since</Label>
                    <p className="font-medium">{format(new Date(createdAt), "MMMM d, yyyy")}</p>
                  </div>
                )}
                <div className="border-t pt-4">
                  <Button variant="destructive" size="sm" onClick={handleSignOut}>
                    <LogOut className="h-4 w-4 mr-1.5" /> Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Lock className="h-4 w-4" /> Change Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="new-pw">New Password</Label>
                    <Input id="new-pw" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    {checks.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {c.pass ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground" />}
                        <span className={c.pass ? "text-green-600" : "text-muted-foreground"}>{c.label}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label htmlFor="confirm-pw">Confirm Password</Label>
                    <Input id="confirm-pw" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <Button type="submit" disabled={changingPw || !allPass || newPassword !== confirmPassword}>
                    {changingPw ? "Updating…" : "Update Password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Saved Searches Tab */}
          <TabsContent value="searches" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Search className="h-4 w-4" /> Saved Searches
                  <Badge variant="secondary" className="ml-auto text-xs">{searches.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {searchesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : searches.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No saved searches yet.</p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {searches.map((s) => (
                      <div key={s.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{s.query}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">{s.source}</Badge>
                            {s.model && <Badge variant="secondary" className="text-[10px]">{s.model}</Badge>}
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(s.created_at), "MMM d, HH:mm")}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteSearch(s.id)}
                          disabled={deletingSearch === s.id}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Download className="h-4 w-4" /> Export Your Data
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">
                  Download your data as CSV files. Each export contains all records associated with your account.
                </p>
                {[
                  { label: "Positions", desc: "All portfolio positions with prices and gains", fn: exportPositions, key: "positions" },
                  { label: "Orders", desc: "Order history with status and details", fn: exportOrders, key: "orders" },
                  { label: "Searches", desc: "Saved search queries and responses", fn: exportSearches, key: "searches" },
                  { label: "Watchlist", desc: "Price alerts and watchlist items", fn: exportWatchlist, key: "watchlist" },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={item.fn} disabled={exporting === item.key}>
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      {exporting === item.key ? "Exporting…" : "CSV"}
                    </Button>
                  </div>
                ))}
                <div className="border-t pt-4">
                  <Button onClick={exportAll} disabled={exporting === "all"} className="w-full">
                    <Download className="h-4 w-4 mr-2" />
                    {exporting === "all" ? "Preparing ZIP…" : "Export All as ZIP"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
